import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

const SEOHelmet = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author = 'phoenix.ai',
  publishedTime,
  modifiedTime,
  section,
  tags,
  locale,
  alternateLocales = ['en', 'zh-HK', 'zh-CN']
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const baseUrl = 'https://phoenix.ai';
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;
  const defaultImage = `${baseUrl}/mbr-1.jpg`;
  const imageUrl = image ? `${baseUrl}${image}` : defaultImage;

  // Generate structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": type === 'article' ? 'Article' : 'WebSite',
    "name": title,
    "description": description,
    "url": fullUrl,
    "image": imageUrl,
    "author": {
      "@type": "Organization",
      "name": author,
      "url": baseUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "phoenix.ai",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/mbr-1.jpg`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullUrl
    }
  };

  if (type === 'article') {
    structuredData.headline = title;
    structuredData.datePublished = publishedTime;
    structuredData.dateModified = modifiedTime || publishedTime;
    if (section) structuredData.articleSection = section;
    if (tags) structuredData.keywords = tags;
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Language and Locale */}
      <html lang={currentLang} />
      <meta name="language" content={currentLang} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Alternate Language URLs */}
      {alternateLocales.map(lang => (
        <link key={lang} rel="alternate" hrefLang={lang} href={`${fullUrl}?lang=${lang}`} />
      ))}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1920" />
      <meta property="og:image:height" content="1080" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="phoenix.ai" />
      <meta property="og:locale" content={currentLang === 'en' ? 'en_US' : currentLang === 'zh-HK' ? 'zh_HK' : 'zh_CN'} />
      
      {/* Article specific Open Graph tags */}
      {type === 'article' && (
        <>
          <meta property="article:author" content={author} />
          <meta property="article:published_time" content={publishedTime} />
          <meta property="article:modified_time" content={modifiedTime || publishedTime} />
          {section && <meta property="article:section" content={section} />}
          {tags && tags.split(',').map(tag => (
            <meta key={tag.trim()} property="article:tag" content={tag.trim()} />
          ))}
        </>
      )}
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:site" content="@phoenix.ai" />
      <meta name="twitter:creator" content="@phoenix.ai" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
      <meta name="application-name" content="phoenix.ai" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
      <link rel="preconnect" href="https://cdnjs.cloudflare.com" />
      
      {/* DNS Prefetch for better performance */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="dns-prefetch" href="//cdnjs.cloudflare.com" />
    </Helmet>
  );
};

export default SEOHelmet;